import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { IInvoice } from "../models/invoice/IInvoice";
import { useAuth } from "../contexts/AuthContext";
import { resourceAPIURL } from "../config";
import { IInvoiceItem } from "../models/Invoice";


export default function useInvoiceMutations() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { claims, token } = useAuth();

  const createItem = useMutation({
    mutationFn: (payload: any) =>
      post<DefaultResponseWithData<IInvoice>>(`/invoice/${payload.invoiceId}/item`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  // POST: Create Invoice
  const createInvoice = useMutation({
    mutationFn: (payload: any) =>
      post<DefaultResponseWithData<IInvoice>>("/invoice", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  // PUT: Update Invoice
  const updateInvoice = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      put<DefaultResponseWithData<IInvoice>>(`/invoice/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  const updateInvoiceItem = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      put<DefaultResponseWithData<IInvoiceItem>>(`/invoice/${id}/item/${payload.id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    }
  });

  // DELETE: Delete Invoice
  const deleteInvoice = useMutation({
    mutationFn: (id: string) =>
      del<DefaultResponseWithData<boolean>>(`/invoice/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  const downloadInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`${resourceAPIURL}/api/invoice/${invoiceId}/download`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download invoice.");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const fileName =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `invoice_${invoiceId}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  };

  const downloadBulkInvoices = async (invoiceIds: string[], service: string) => {
    try {
      const response = await fetch(`${resourceAPIURL}/api/invoice/export/${service}/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: invoiceIds })
      });

      if (!response.ok) {
        // The Oracle export refuses with an actionable message - which account is missing which
        // Oracle code, which setting is unset - so pass it on instead of a generic failure.
        const message = await response
          .clone()
          .json()
          .then((body) => body?.message)
          .catch(() => undefined);

        throw new Error(message || "Failed to download invoices.");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const currentTimestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const extension = service === "oracle" ? "xlsx" : "csv";
      const fileName =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `invoices_${currentTimestamp}.${extension}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  }

  return {
    createInvoice,
    updateInvoice,
    deleteInvoice,
    downloadInvoice,
    downloadBulkInvoices,

    createItem,

    updateInvoiceItem
  };
}
